/******************************************************************************
 *** BgtCmptnAttachDetail (공통 상세 컴포넌트)
*******************************************************************************/
import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import Button from "@/js/common/ui/Button";
import BudgetFile from "../../budgetFileBox/BudgetFile";
import { budgetFilesProps } from "@/js/common/types/types";

interface Props {
    mainTab: "OVRL" | "MBSDS" | "SPC";
    subTab: "DPST" | "EXPND";
    docId: string;
}

const rowLabelStyle: React.CSSProperties = {
    width: 110,
    minWidth: 110,
    backgroundColor: "#F7F7F7",
    borderRight: "1px solid #D9D9D9",
    padding: "12px 14px",
    fontSize: 13,
    fontWeight: 700,
    color: "#333333",
    boxSizing: "border-box",
};

const rowValueStyle: React.CSSProperties = {
    flex: 1,
    padding: "12px 14px",
    fontSize: 13,
    color: "#222222",
    boxSizing: "border-box",
    wordBreak: "break-word",
};

const docRowStyle: React.CSSProperties = {
    display: "flex",
    borderBottom: "1px solid #D9D9D9",
};

const sectionTitleStyle: React.CSSProperties = {
    fontSize: 15,
    fontWeight: 700,
    color: "#222222",
    letterSpacing: -0.3,
};

const getExpndRsdocSeNm = (expndRsdocSeCd: string) => {
    if (expndRsdocSeCd === "01") return "일반";
    if (expndRsdocSeCd === "02") return "무증빙";
    if (expndRsdocSeCd === "03") return "여입";
    if (expndRsdocSeCd === "04") return "자료제출";
    return "";
};

const BgtCmptnAttachDetail = ({ mainTab, subTab, docId }: Props) => {
    const [data, setData] = React.useState<any>({});

    const [budgetFiles, setBudgetFiles] = React.useState<File[]>([]);
    const [savedBudgetFiles, setSavedBudgetFiles] = React.useState<budgetFilesProps[]>([]);

    const [evdncFiles, setEvdncFiles] = React.useState<File[]>([]);
    const [savedEvdncFiles, setSavedEvdncFiles] = React.useState<budgetFilesProps[]>([]);

    const [evdncList, setEvdncList] = React.useState<any[]>([]);
    const [evdncSumUseAmt, setEvdncSumUseAmt] = React.useState<string>("0");
    const [hoveredRow, setHoveredRow] = React.useState<number | null>(null);

    const [selectedEvdncId, setSelectedEvdncId] = React.useState<string>("");
    const [expndEvdncRsdocNo, setExpndEvdncRsdocNo] = React.useState<string>("");
    const [evdncView, setEvdncView] = React.useState<string>("N");

    const [useDt, setUseDt] = React.useState<string>("");
    const [usePrpsCn, setUsePrpsCn] = React.useState<string>("");
    const [useCn, setUseCn] = React.useState<string>("");
    const [useAmt, setUseAmt] = React.useState<string>("");
    const [etcCn, setEtcCn] = React.useState<string>("");
    const [rmrkCn, setRmrkCn] = React.useState<string>("");

    const getDtlApiUrl = () => {
        if (mainTab === "OVRL" && subTab === "DPST") return "system/bgt/ovrl/whol/select-ovrl-whol-dpst-dtl.do";
        if (mainTab === "OVRL" && subTab === "EXPND") return "system/bgt/ovrl/whol/select-ovrl-whol-expnd-dtl.do";
        if (mainTab === "MBSDS" && subTab === "DPST") return "system/bgt/mbsds/dpst/select-bgt-mbsds-dpst-dtl.do";
        if (mainTab === "MBSDS" && subTab === "EXPND") return "system/bgt/mbsds/expnd/select-bgt-mbsds-expnd-rsdoc-dtl.do";
        if (mainTab === "SPC" && subTab === "DPST") return "system/bgt/spc/dpst/select-bgt-spc-dpst-dtl.do";
        return "system/bgt/spc/expnd/select-bgt-spc-expnd-rsdoc-dtl.do";
    };

    const getDtlParam = () => {
        if (mainTab === "OVRL" && subTab === "DPST") return { ovrlMbsdsDpstDocId: docId };
        if (mainTab === "OVRL" && subTab === "EXPND") return { ovrlMbsdsExpndDocId: docId };
        if (mainTab === "MBSDS" && subTab === "DPST") return { mbsdsDpstDocId: docId };
        if (mainTab === "MBSDS" && subTab === "EXPND") return { mbsdsExpndDocId: docId };
        if (mainTab === "SPC" && subTab === "DPST") return { spcAcntgDpstDocId: docId };
        return { spcAcntgExpndDocId: docId };
    };

    const getFileSeCd = () => {
        if (mainTab === "OVRL" && subTab === "DPST") return "C01";
        if (mainTab === "OVRL" && subTab === "EXPND") return "C02";
        if (mainTab === "MBSDS" && subTab === "DPST") return "A01";
        if (mainTab === "MBSDS" && subTab === "EXPND") return "A02";
        if (mainTab === "SPC" && subTab === "DPST") return "B01";
        return "B02";
    };

    const getEvdncDtlApiUrl = () => {
        if (mainTab === "MBSDS") return "system/bgt/mbsds/expnd/select-bgt-evdnc.do";
        return "system/bgt/spc/expnd/select-bgt-spc-evdnc.do";
    };

    const getEvdncDtlParam = (evdncId: string) => {
        if (mainTab === "MBSDS") return { expndEvdncId: evdncId };
        return { spcAcntgExpndEvdncId: evdncId };
    };

    const getEvdncId = (row: any) => {
        if (mainTab === "MBSDS") return row.expndEvdncId;
        return row.spcAcntgExpndEvdncId;
    };

    const getAcntgYear = () => {
        if (subTab === "DPST") {
            return Util.getAcntgYear(data.dpstDt);
        }
        return Util.getAcntgYear(data.implDt);
    };

    const showEvdncArea = mainTab !== "OVRL" && subTab === "EXPND";

    React.useEffect(() => {
        if (!docId) return;

        AppContext.showSpinner();

        Axios.dataAccess({
            url: getDtlApiUrl(),
            methodType: "post",
            paramType: "json",
            paramData: getDtlParam(),
            onSuccessFn: (res: any) => {
                setData(res?.result?.item || {});
                setSavedBudgetFiles(res?.result?.listFile || []);
                setBudgetFiles([]);
                setEvdncFiles([]);

                if (showEvdncArea) {
                    setEvdncList(res?.result?.list || []);
                    setEvdncSumUseAmt(res?.result?.listSum?.sumUseAmt || "0");
                } else {
                    setEvdncList([]);
                    setEvdncSumUseAmt("0");
                }

                setSelectedEvdncId("");
                setExpndEvdncRsdocNo("");
                setEvdncView("N");
                setUseDt("");
                setUsePrpsCn("");
                setUseCn("");
                setUseAmt("");
                setEtcCn("");
                setRmrkCn("");
                setSavedEvdncFiles([]);

                AppContext.hideSpinner();
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docId, mainTab, subTab]);

    React.useEffect(() => {
        if (!selectedEvdncId || !showEvdncArea) return;

        AppContext.showSpinner();

        Axios.dataAccess({
            url: getEvdncDtlApiUrl(),
            methodType: "post",
            paramType: "json",
            paramData: getEvdncDtlParam(selectedEvdncId),
            onSuccessFn: (res: any) => {
                const item = res?.result?.item || {};

                setExpndEvdncRsdocNo(item.expndEvdncRsdocNo || "");
                setUseDt(item.useDt || "");
                setUsePrpsCn(item.usePrpsCn || "");
                setUseCn(item.useCn || "");
                setUseAmt(item.useAmt || "");
                setEtcCn(item.etcCn || "");
                setRmrkCn(item.rmrkCn || "");
                setSavedEvdncFiles(res?.result?.listFile || []);
                setEvdncFiles([]);
                setEvdncView("Y");

                AppContext.hideSpinner();
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEvdncId]);

    function saveFiles(refId: string, fileSeCd: string, acntgYear: string, saveFiles: File[]) {
        if (Util.isEmpty(refId)) {
            AppContext.openAlert({
                title: "첨부파일 확인",
                msg: "참조 문서 ID가 없습니다.",
                icon: "warning",
            });
            return;
        }

        if (Util.isEmpty(acntgYear)) {
            AppContext.openAlert({
                title: "회계연도 확인",
                msg: "회계연도 정보가 없습니다.",
                icon: "warning",
            });
            return;
        }

        if (Util.isEmpty(saveFiles) || saveFiles.length === 0) {
            AppContext.openAlert({
                title: "첨부파일 확인",
                msg: "업로드할 파일이 없습니다.",
                icon: "warning",
            });
            return;
        }

        AppContext.showSpinner();

        Axios.dataAccess({
            url: "system/com/file/upload.do",
            methodType: "post",
            isMultipart: true,
            paramData: {
                refId: refId,
                fileSeCd: fileSeCd,
                acntgYear: acntgYear,
                budgetFiles: saveFiles,
            },
            onSuccessFn: () => {
                AppContext.openAlert({
                    title: "첨부파일 저장",
                    msg: "첨부파일이 저장되었습니다.",
                    icon: "check",
                });

                if (refId === docId) {
                    Axios.dataAccess({
                        url: getDtlApiUrl(),
                        methodType: "post",
                        paramType: "json",
                        paramData: getDtlParam(),
                        onSuccessFn: (res: any) => {
                            setSavedBudgetFiles(res?.result?.listFile || []);
                            setBudgetFiles([]);
                            AppContext.hideSpinner();
                        },
                    });
                } else {
                    Axios.dataAccess({
                        url: getEvdncDtlApiUrl(),
                        methodType: "post",
                        paramType: "json",
                        paramData: getEvdncDtlParam(refId),
                        onSuccessFn: (res: any) => {
                            setSavedEvdncFiles(res?.result?.listFile || []);
                            setEvdncFiles([]);
                            AppContext.hideSpinner();
                        },
                    });
                }
            },
        });
    }

    function onClickSaveRsdoc() {
        saveFiles(docId, getFileSeCd(), getAcntgYear(), budgetFiles);
    }

    function onClickSaveEvdnc() {
        if (Util.isEmpty(selectedEvdncId)) {
            AppContext.openAlert({
                title: "지출증빙 확인",
                msg: "지출증빙을 먼저 선택하세요.",
                icon: "warning",
            });
            return;
        }

        saveFiles(
            selectedEvdncId,
            mainTab === "MBSDS" ? "A03" : "B03",
            getAcntgYear(),
            evdncFiles
        );
    }

    const date = subTab === "DPST" ? data.dpstDt : data.implDt;
    const amt = subTab === "DPST" ? data.dpstAmt : data.implAmt;
    const rsdocNo = subTab === "DPST" ? data.dpstRsdocNo : data.expndRsdocNo;

    const expndRsdocSeCd = data.expndRsdocSeCd || "";
    const dataSbmsnCmptnYn = data.dataSbmsnCmptnYn || "";
    const lnkgExpndRsdocNo = data.lnkgExpndRsdocNo || "";

    return (
        <div className="w100p dpFlx jcC">
            <div className="w500 pt10">
                <div
                    style={{
                        border: "1px solid #CFCFCF",
                        backgroundColor: "#FFFFFF",
                    }}
                >
                    {/* 문서 헤더 */}
                    <div
                        className="dpFlx jcSb aiC"
                        style={{
                            padding: "18px 20px 14px 20px",
                            borderBottom: "2px solid #444444",
                        }}
                    >
                        <div>
                            <div className="fm-b fs20 fc222222">
                                {subTab === "DPST" ? "수입결의서" : "지출결의서"}
                            </div>
                            {subTab === "EXPND" && !!expndRsdocSeCd && (
                                <div className="mt6 fm-c fs13 fc666666">
                                    구분 : {getExpndRsdocSeNm(expndRsdocSeCd)}
                                    {expndRsdocSeCd === "02" && !!dataSbmsnCmptnYn ? ` / ${dataSbmsnCmptnYn}` : ""}
                                </div>
                            )}
                        </div>

                        <div className="fm-c fs13 fc444444">
                            문서번호 : <span className="fm-b">{rsdocNo || "-"}</span>
                        </div>
                    </div>

                    {/* 기본정보 */}
                    <div style={{ borderBottom: "1px solid #D9D9D9" }}>
                        <div style={docRowStyle}>
                            <div style={rowLabelStyle}>{subTab === "DPST" ? "입금일자" : "행위년월일"}</div>
                            <div style={rowValueStyle}>{Util.cvtDateStrForm(String(date), "-", "YYYYMMDD")}</div>
                            <div style={rowLabelStyle}>금액</div>
                            <div style={{ ...rowValueStyle, fontWeight: 700 }}>
                                {Util.comma(String(amt || 0))}원
                            </div>
                        </div>

                        {subTab === "EXPND" && (expndRsdocSeCd === "03" || expndRsdocSeCd === "04") && (
                            <div style={docRowStyle}>
                                <div style={rowLabelStyle}>연결 문서번호</div>
                                <div style={rowValueStyle}>{lnkgExpndRsdocNo || "-"}</div>
                            </div>
                        )}

                        <div style={docRowStyle}>
                            <div style={rowLabelStyle}>적요</div>
                            <div style={rowValueStyle}>{data.abstrCn || "-"}</div>
                        </div>

                        <div style={docRowStyle}>
                            <div style={rowLabelStyle}>메모</div>
                            <div style={rowValueStyle}>{data.memo || "-"}</div>
                        </div>
                    </div>

                    {/* 첨부파일 */}
                    <div style={{ padding: "16px 20px", borderBottom: showEvdncArea ? "1px solid #D9D9D9" : "0" }}>
                        <div className="dpFlx jcSb aiC mb10">
                            <span style={sectionTitleStyle}>첨부파일</span>
                            <Button
                                buttonName="저장"
                                width={90}
                                height={34}
                                fontFamily="fm-b"
                                fontSize={13}
                                letterSpacing={-1}
                                variant="styCompleteBtn"
                                borderRadius={4}
                                buttonClass="dpFlx aiC jcC"
                                onClickFunc={onClickSaveRsdoc}
                            />
                        </div>

                        <BudgetFile
                            atrzSttsCd={"01"}
                            fileSeCd={getFileSeCd()}
                            savedBudgetFiles={savedBudgetFiles}
                            setSavedBudgetFiles={setSavedBudgetFiles}
                            budgetFiles={budgetFiles}
                            setBudgetFiles={setBudgetFiles}
                            width={456}
                        />
                    </div>

                    {/* 지출증빙 */}
                    {showEvdncArea && (
                        <>
                            <div style={{ padding: "16px 20px", borderBottom: "1px solid #D9D9D9" }}>
                                <div className="dpFlx jcSb aiC mb10">
                                    <span style={sectionTitleStyle}>지출증빙 내역서</span>
                                    {evdncView === "Y" && (
                                        <Button
                                            buttonName="상세 숨기기"
                                            width={100}
                                            height={30}
                                            fontFamily="fm-c"
                                            fontSize={12}
                                            letterSpacing={-0.3}
                                            color="#555555"
                                            backgroundColor="#F5F5F5"
                                            borderRadius={4}
                                            buttonClass="dpFlx aiC jcC bd1"
                                            onClickFunc={() => {
                                                setEvdncView("N");
                                                setSelectedEvdncId("");
                                            }}
                                        />
                                    )}
                                </div>

                                <div className="mb8 taR">
                                    <span className="fm-c fs12 fc666666">합계 </span>
                                    <span className="fm-b fs13 fc222222">
                                        {Util.comma(String(evdncSumUseAmt || 0))}원
                                    </span>
                                </div>

                                <div style={{ border: "1px solid #D9D9D9" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <colgroup>
                                            <col style={{ width: "25%" }} />
                                            <col style={{ width: "45%" }} />
                                            <col style={{ width: "30%" }} />
                                        </colgroup>
                                        <thead>
                                            <tr style={{ backgroundColor: "#F7F7F7", borderBottom: "1px solid #D9D9D9" }}>
                                                <th className="h36 fm-b fs12 fc444444">사용일자</th>
                                                <th className="fm-b fs12 fc444444">사용목적</th>
                                                <th className="fm-b fs12 fc444444">금액</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {evdncList.length === 0 && (
                                                <tr>
                                                    <td
                                                        colSpan={3}
                                                        className="h40 taC fm-c fs12 fc999999"
                                                        style={{ borderBottom: "1px solid #D9D9D9" }}
                                                    >
                                                        조회된 지출증빙내역서가 없습니다.
                                                    </td>
                                                </tr>
                                            )}

                                            {evdncList.map((row, index) => {
                                                const isSelected = selectedEvdncId === getEvdncId(row);

                                                return (
                                                    <tr
                                                        key={index}
                                                        className="csP"
                                                        onMouseOver={() => setHoveredRow(index)}
                                                        onMouseOut={() => setHoveredRow(null)}
                                                        onClick={() => setSelectedEvdncId(getEvdncId(row))}
                                                        style={{
                                                            borderBottom: "1px solid #E5E5E5",
                                                            backgroundColor: isSelected
                                                                ? "#F3F6FA"
                                                                : hoveredRow === index
                                                                ? "#FAFAFA"
                                                                : "#FFFFFF",
                                                        }}
                                                    >
                                                        <td className="h38 taC fm-c fs12 fc333333">
                                                            {Util.cvtDateStrForm(String(row.useDt), "-", "YYYYMMDD")}
                                                        </td>
                                                        <td className="pl10 pr10 fm-c fs12 fc333333 toE">
                                                            {row.usePrpsCn || "-"}
                                                        </td>
                                                        <td className="pr12 taR fm-b fs12 fc333333">
                                                            {Util.comma(String(row.useAmt || 0))}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {evdncView === "Y" && (
                                <div style={{ padding: "16px 20px" }}>
                                    <div className="dpFlx jcSb aiC mb10">
                                        <div>
                                            <div style={sectionTitleStyle}>지출증빙 상세</div>
                                            <div className="mt4 fm-c fs12 fc666666">
                                                문서번호 : {expndEvdncRsdocNo}
                                            </div>
                                        </div>

                                        <Button
                                            buttonName="저장"
                                            width={90}
                                            height={34}
                                            fontFamily="fm-b"
                                            fontSize={13}
                                            letterSpacing={-1}
                                            variant="styCompleteBtn"
                                            borderRadius={4}
                                            buttonClass="dpFlx aiC jcC"
                                            onClickFunc={onClickSaveEvdnc}
                                        />
                                    </div>

                                    <div style={{ border: "1px solid #D9D9D9", marginBottom: 14 }}>
                                        <div style={docRowStyle}>
                                            <div style={rowLabelStyle}>사용일자</div>
                                            <div style={rowValueStyle}>{Util.cvtDateStrForm(String(useDt), "-", "YYYYMMDD")}</div>
                                            <div style={rowLabelStyle}>사용금액</div>
                                            <div style={{ ...rowValueStyle, fontWeight: 700 }}>
                                                {Util.comma(String(useAmt || 0))}원
                                            </div>
                                        </div>

                                        <div style={docRowStyle}>
                                            <div style={rowLabelStyle}>사용목적</div>
                                            <div style={rowValueStyle}>{usePrpsCn || "-"}</div>
                                        </div>

                                        <div style={docRowStyle}>
                                            <div style={rowLabelStyle}>사용내용</div>
                                            <div style={rowValueStyle}>{useCn || "-"}</div>
                                        </div>

                                        <div style={docRowStyle}>
                                            <div style={rowLabelStyle}>기타내용</div>
                                            <div style={rowValueStyle}>{etcCn || "-"}</div>
                                        </div>

                                        <div style={{ ...docRowStyle, borderBottom: "0" }}>
                                            <div style={rowLabelStyle}>비고</div>
                                            <div style={rowValueStyle}>{rmrkCn || "-"}</div>
                                        </div>
                                    </div>

                                    <BudgetFile
                                        atrzSttsCd="04"
                                        fileSeCd={mainTab === "MBSDS" ? "A03" : "B03"}
                                        savedBudgetFiles={savedEvdncFiles}
                                        setSavedBudgetFiles={setSavedEvdncFiles}
                                        budgetFiles={evdncFiles}
                                        setBudgetFiles={setEvdncFiles}
                                        width={456}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BgtCmptnAttachDetail;